<template>
  <div class="nd-page">
    <header class="nd-page-header">
      <div>
        <h1 class="page-title">{{ pageTitle }}</h1>
        <p class="nd-page-kicker">Monte propostas, valores e condições comerciais para o paciente.</p>
      </div>
    </header>

    <v-card v-if="guardianInfo.isMinor" class="nd-section-card guardian-card">
      <div class="guardian-card__header">
        <v-icon color="#A04DC2">mdi-account-child-outline</v-icon>
        <strong>Paciente menor de 18 anos</strong>
        <span>Orçamento em nome do responsável</span>
      </div>
      <div class="guardian-card__grid">
        <div class="account-info">
          <span>Responsável</span>
          <strong>{{ guardianInfo.name || "—" }}</strong>
        </div>
        <div class="account-info">
          <span>CPF</span>
          <strong>{{ guardianInfo.cpf || "—" }}</strong>
        </div>
        <div class="account-info">
          <span>Telefone</span>
          <strong>{{ guardianInfo.phone || "—" }}</strong>
        </div>
      </div>
    </v-card>

    <v-card class="nd-section-card">
      <v-form ref="proposalForm" v-model="formValid">
        <div class="nd-table-toolbar">
          <div>
            <div class="nd-table-toolbar__title">Dados do orçamento</div>
            <div class="nd-table-toolbar__subtitle">Defina paciente, status, pagamento, valor e procedimentos.</div>
          </div>
        </div>

        <div class="nd-form-grid nd-form-grid--two">
          <v-autocomplete
            label="Paciente"
            v-model="proposal.patientId"
            :items="patientItems"
            :search-input.sync="patientSearch"
            :loading="loadingPatients"
            item-text="name"
            item-value="id"
            :rules="[rules.required]"
            outlined
            dense
            no-data-text="Nenhum paciente encontrado"
            clearable
            @update:search-input="searchPatients"
            @change="onPatientChanged"
           hide-details="auto"></v-autocomplete>
          <v-select
            label="Status"
            v-model="proposal.status"
            :items="statusOptions"
            item-text="label"
            item-value="value"
            :rules="[rules.required]"
            outlined
            dense
           hide-details="auto"></v-select>
          <v-select
            label="Forma de pagamento"
            v-model="proposal.paymentMethod"
            :items="paymentMethodOptions"
            item-text="label"
            item-value="value"
            outlined
            dense
            clearable
           hide-details="auto"></v-select>
          <v-text-field
            label="Valor total"
            v-model="proposal.totalValue"
            type="number"
            step="0.01"
            min="0"
            outlined
            dense
            prefix="R$"
           hide-details="auto"></v-text-field>
          <v-textarea
            class="nd-form-grid__wide"
            label="Descrição do orçamento"
            v-model="proposal.description"
            outlined
            dense
            rows="3"
            auto-grow
           hide-details="auto"></v-textarea>
        </div>

        <section class="proposal-procedures">
          <div class="proposal-procedures__header">
            <div>
              <h3>Procedimentos do orçamento</h3>
              <p>Selecione o procedimento e detalhe a execução ou condição comercial.</p>
            </div>
          </div>

          <div class="proposal-procedures__form">
            <v-autocomplete
              label="Procedimento"
              v-model="procedureDraft.procedureId"
              :items="procedureItems"
              :loading="loadingProcedures"
              item-text="name"
              item-value="id"
              outlined
              dense
              clearable
              no-data-text="Nenhum procedimento encontrado"
              hide-details="auto"
            ></v-autocomplete>
            <v-text-field
              label="Descrição"
              v-model="procedureDraft.description"
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
            <v-btn type="button" color="primary" elevation="0" @click="addProcedureItem">
              <v-icon left size="18">mdi-plus</v-icon>
              Adicionar
            </v-btn>
          </div>

          <div v-if="proposal.procedures.length" class="proposal-procedures__list">
            <div v-for="(item, index) in proposal.procedures" :key="item.localId || index" class="proposal-procedure-item">
              <div>
                <strong>{{ item.name || procedureName(item.procedureId) }}</strong>
                <span>{{ item.description || "Sem descrição informada." }}</span>
              </div>
              <v-btn icon small color="error" @click="removeProcedureItem(index)">
                <v-icon size="18">mdi-trash-can-outline</v-icon>
              </v-btn>
            </div>
          </div>
          <div v-else class="proposal-procedures__empty">
            Nenhum procedimento adicionado ao orçamento.
          </div>
        </section>

        <div class="nd-form-actions">
          <v-btn type="button" color="primary" outlined elevation="0" @click="$router.push('/orcamentos')">
            Voltar
          </v-btn>
          <v-btn type="button" color="primary" elevation="0" @click="saveProposal" :loading="loadingSave">
            Salvar
          </v-btn>
        </div>
      </v-form>
    </v-card>

    <v-overlay :value="loadingPage">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ProposalsDetails",

  data: () => ({
    proposal: {
      id: null,
      patientId: null,
      status: 1,
      paymentMethod: null,
      totalValue: null,
      description: "",
      procedures: [],
    },

    procedureDraft: {
      procedureId: null,
      description: "",
    },

    guardianInfo: {
      isMinor: false,
      name: null,
      cpf: null,
      phone: null,
      rawCpf: null,
      rawPhone: null,
    },

    patientItems: [],
    patientSearch: "",
    loadingPatients: false,
    procedureItems: [],
    loadingProcedures: false,
    searchTimeout: null,

    statusOptions: [
      { label: "Pendente",   value: 1 },
      { label: "Aprovado",   value: 2 },
      { label: "Rejeitado",  value: 3 },
      { label: "Aguardando", value: 4 },
    ],
    paymentMethodOptions: [
      { label: "Dinheiro",          value: 1 },
      { label: "Cartão de Crédito", value: 2 },
      { label: "Cartão de Débito",  value: 3 },
      { label: "Boleto",            value: 4 },
      { label: "Pix",               value: 5 },
    ],

    loadingPage: false,
    loadingSave: false,
    formValid: false,

    rules: {
      required: (value) => !!value || "Campo obrigatório.",
    },
  }),

  computed: {
    pageTitle() {
      return this.proposal.id ? "Editar Orçamento" : "Novo Orçamento";
    },
    breadcrumbs() {
      return [
        { text: "Home", disabled: false, to: "/home", exact: true },
        { text: "Orçamentos", disabled: false, to: "/orcamentos", exact: true },
        { text: this.pageTitle, disabled: true, exact: true },
      ];
    },
  },

  async created() {
    const id = this.$route.params.id;
    await this.loadProcedures();
    if (id) {
      this.loadingPage = true;
      await this.loadProposal(id);
      this.loadingPage = false;
    }
  },

  methods: {
    searchPatients(val) {
      if (!val || val.length < 2) return;
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.fetchPatients(val), 300);
    },

    async fetchPatients(search) {
      this.loadingPatients = true;
      try {
        const result = await axios.get("/patients", { params: { search, limit: 20, start: 0 } });
        this.patientItems = (result.data.data || []).map((p) => ({ id: p.id, name: p.name }));
      } catch {
        // silent
      } finally {
        this.loadingPatients = false;
      }
    },

    async loadProcedures() {
      this.loadingProcedures = true;
      try {
        const result = await axios.get("/procedures", { params: { start: 0, limit: 200 } });
        this.procedureItems = this.normalizeList(result).map((item) => ({
          id: item.id,
          name: item.name,
        }));
      } catch (error) {
        this.$dialog.notify.error("Erro ao carregar procedimentos.", { position: "top-right", timeout: 5000 });
      } finally {
        this.loadingProcedures = false;
      }
    },

    normalizeList(response) {
      const payload = response && response.data;
      if (Array.isArray(payload)) return payload;
      if (payload && Array.isArray(payload.data)) return payload.data;
      if (payload && payload.data && Array.isArray(payload.data.data)) return payload.data.data;
      return [];
    },

    async onPatientChanged(patientId) {
      if (!patientId) {
        this.guardianInfo = { isMinor: false, name: null, cpf: null, phone: null, rawCpf: null, rawPhone: null };
        return;
      }
      await this.fetchPatientGuardian(patientId, null);
    },

    async fetchPatientGuardian(patientId, existingBilling) {
      try {
        const result = await axios.get(`/patients/${patientId}`);
        const p = result.data.data;
        const isMinor = this._calcIsMinor(p.birthday);
        const rawCpf = isMinor ? (existingBilling?.billingCpf || p.guardianCpf || null) : null;
        const rawPhone = isMinor ? (existingBilling?.billingPhone || p.guardianPhone || null) : null;
        this.guardianInfo = {
          isMinor,
          name: isMinor ? (existingBilling?.billingName || p.guardianName || null) : null,
          cpf: this._formatCpf(rawCpf),
          phone: this._formatPhone(rawPhone),
          rawCpf,
          rawPhone,
        };
      } catch {
        this.guardianInfo = { isMinor: false, name: null, cpf: null, phone: null, rawCpf: null, rawPhone: null };
      }
    },

    _calcIsMinor(birthday) {
      if (!birthday) return false;
      const b = new Date(birthday);
      if (isNaN(b.getTime())) return false;
      const today = new Date();
      let age = today.getFullYear() - b.getFullYear();
      const m = today.getMonth() - b.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
      return age < 18;
    },

    _formatCpf(raw) {
      if (!raw) return null;
      const d = raw.replace(/\D/g, '');
      if (d.length === 11) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
      return raw;
    },

    _formatPhone(raw) {
      if (!raw) return null;
      const d = raw.replace(/\D/g, '');
      if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
      if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
      return raw;
    },

    procedureName(procedureId) {
      const procedure = this.procedureItems.find((item) => Number(item.id) === Number(procedureId));
      return procedure ? procedure.name : "Procedimento";
    },

    addProcedureItem() {
      if (!this.procedureDraft.procedureId) {
        this.$dialog.notify.warn("Selecione um procedimento para adicionar ao orçamento.", {
          position: "top-right",
          timeout: 3000,
        });
        return;
      }

      const procedure = this.procedureItems.find((item) => Number(item.id) === Number(this.procedureDraft.procedureId));
      this.proposal.procedures.push({
        localId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        procedureId: this.procedureDraft.procedureId,
        name: procedure ? procedure.name : null,
        description: this.procedureDraft.description || "",
      });

      this.procedureDraft = {
        procedureId: null,
        description: "",
      };
    },

    removeProcedureItem(index) {
      this.proposal.procedures.splice(index, 1);
    },

    normalizeProcedures(value) {
      if (!value) return [];
      if (Array.isArray(value)) {
        return value.map((item) => {
          if (typeof item === "string") {
            return {
              localId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
              procedureId: null,
              name: item,
              description: "",
            };
          }

          return {
            localId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            procedureId: item.procedureId || item.id || item.procedure_id || null,
            name: item.name || item.procedureName || item.label || null,
            description: item.description || item.observation || "",
          };
        });
      }

      return [{
        localId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        procedureId: null,
        name: String(value),
        description: "",
      }];
    },

    buildProceduresPayload() {
      return this.proposal.procedures.map((item) => ({
        procedureId: item.procedureId || null,
        name: item.name || this.procedureName(item.procedureId),
        description: item.description || "",
      }));
    },

    async loadProposal(id) {
      try {
        const result = await axios.get(`/proposals/${id}`);
        const d = result.data.data;
        this.proposal = {
          id: d.id,
          patientId: d.patient ? d.patient.id : null,
          status: d.status,
          paymentMethod: d.paymentMethod,
          totalValue: d.totalValue,
          description: d.description || "",
          procedures: this.normalizeProcedures(d.procedures),
        };
        if (d.patient) {
          this.patientItems = [{ id: d.patient.id, name: d.patient.name }];
          await this.fetchPatientGuardian(d.patient.id, {
            billingName: d.billingName,
            billingCpf: d.billingCpf,
            billingPhone: d.billingPhone,
          });
        }
      } catch {
        this.$dialog.notify.error("Erro ao carregar orçamento.", { position: "top-right", timeout: 5000 });
      }
    },

    async saveProposal() {
      if (!this.$refs.proposalForm.validate()) return;

      this.loadingSave = true;
      try {
        const billing = this.guardianInfo.isMinor ? {
          billingName: this.guardianInfo.name || null,
          billingCpf: this.guardianInfo.rawCpf || null,
          billingPhone: this.guardianInfo.rawPhone || null,
        } : {
          billingName: null,
          billingCpf: null,
          billingPhone: null,
        };

        if (this.proposal.id) {
          const payload = {
            operation: 'update',
            status: this.proposal.status,
            paymentMethod: this.proposal.paymentMethod,
            totalValue: this.proposal.totalValue ? Number(this.proposal.totalValue) : null,
            description: this.proposal.description || null,
            procedures: this.buildProceduresPayload(),
            ...billing,
          };
          await axios.patch(`/proposals/${this.proposal.id}`, payload);
          this.$dialog.notify.success("Orçamento atualizado com sucesso!", { position: "top-right", timeout: 3000 });
        } else {
          const payload = {
            patient: this.proposal.patientId,
            status: this.proposal.status,
            paymentMethod: this.proposal.paymentMethod,
            totalValue: this.proposal.totalValue ? Number(this.proposal.totalValue) : null,
            description: this.proposal.description || null,
            procedures: this.buildProceduresPayload(),
            ...billing,
          };
          const result = await axios.post("/proposals", payload);
          this.$dialog.notify.success("Orçamento criado com sucesso!", { position: "top-right", timeout: 3000 });
          this.$router.replace(`/orcamentos/${result.data.data.id}`);
        }
      } catch {
        this.$dialog.notify.error("Erro ao salvar orçamento.", { position: "top-right", timeout: 5000 });
      } finally {
        this.loadingSave = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.proposal-procedures {
  border-top: 1px solid #eef0f2;
  margin-top: 24px;
  padding: 24px 24px 0;
}

.proposal-procedures__header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;

  h3 {
    color: #101c1a;
    font-size: 18px;
    margin: 0 0 4px;
  }

  p {
    color: #6f7a76;
    margin: 0;
  }
}

.proposal-procedures__form {
  align-items: stretch;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(220px, 0.8fr) minmax(280px, 1.2fr) max-content;

  .v-btn {
    min-height: 40px;
  }
}

.proposal-procedures__list {
  display: grid;
  gap: 10px;
  margin-top: 16px;
}

.proposal-procedure-item {
  align-items: center;
  background: #f7f8f7;
  border: 1px solid #eef0f2;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  padding: 14px 16px;

  strong,
  span {
    display: block;
  }

  strong {
    color: #101c1a;
    font-size: 14px;
  }

  span {
    color: #6f7a76;
    margin-top: 3px;
  }
}

.proposal-procedures__empty {
  background: #f7f8f7;
  border: 1px dashed #dfe4e1;
  border-radius: 8px;
  color: #6f7a76;
  margin-top: 16px;
  padding: 18px;
  text-align: center;
}

@media (max-width: 900px) {
  .proposal-procedures__form {
    grid-template-columns: 1fr;
  }

  .proposal-procedures__form .v-btn {
    width: 100%;
  }
}
</style>
